import React, { useMemo, useState, type PropsWithChildren, type Ref, Fragment } from 'react';

import noop from 'lodash/noop';
import Button from '@atlaskit/button/standard-button';
import ShareIcon from '@atlaskit/icon/core/migration/share';
import { IconButton } from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';
import { FabricElementsAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import ShareIconNew from '@atlaskit/icon/core/share';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { getUserLocation } from '@atlassian/jira-platform-router-utils/src/common/utils/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyAfterPaint } from '@atlassian/react-loosely-lazy';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { Trigger, usePanel } from '@atlassian/entry-point-panel';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import {
	COMPANY_MANAGED_PROJECT,
	TEAM_MANAGED_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import type {
	LegacyShareClientConfig,
	RenderCustomTriggerButton,
	CustomTriggerButtonProps,
} from './common/types.tsx';
import { messages } from './messages.tsx';
import SyncShareButtonBase, { type Props } from './ui/index.tsx';
import { shareButtonEntryPoint } from './entrypoint.tsx';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { LegacyShareClientConfig, RenderCustomTriggerButton, CustomTriggerButtonProps };

type LoaderProps = {
	triggerButtonStyle?: 'icon-only' | 'icon-with-text';
};

export const ShareLoadingPlaceholder = ({ triggerButtonStyle = 'icon-only' }: LoaderProps) => {
	const { formatMessage } = useIntl();

	return (
		<div>
			<Button
				appearance="subtle"
				iconBefore={
					<ShareIcon
						LEGACY_size="medium"
						spacing="spacious"
						label={formatMessage(messages.shareTriggerButtonText)}
						color={token('color.icon')}
					/>
				}
				isDisabled
			>
				{triggerButtonStyle === 'icon-with-text'
					? formatMessage(messages.shareTriggerButtonText)
					: null}
			</Button>
		</div>
	);
};

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const ShareButtonBase = lazyAfterPaint(
	() => import(/* webpackChunkName: "async-share-button" */ './ui'),
	{ ssr: false },
);

const ShareButtonAnalyticsWrapper: React.FunctionComponent<PropsWithChildren> = ({ children }) => {
	const { data } = useProjectContext();
	const projectKey = data?.projectKey || '';
	const projectType = data?.projectType;
	const projectStyle = data?.simplified ? TEAM_MANAGED_PROJECT : COMPANY_MANAGED_PROJECT;
	const userLocation = getUserLocation();
	const isAdmin = useIsAdmin();
	const isProjectAdmin = data?.isProjectAdmin || false;

	const analyticsData = useMemo(() => {
		const projectContextData = projectKey
			? { projectType, projectStyle, isProjectAdmin }
			: undefined;

		return {
			isAdmin,
			userLocation,
			...projectContextData,
		};
	}, [projectKey, projectType, projectStyle, isProjectAdmin, userLocation, isAdmin]);

	return (
		<ContextualAnalyticsData attributes={analyticsData}>
			<FabricElementsAnalyticsContext data={analyticsData}>
				{children}
			</FabricElementsAnalyticsContext>
		</ContextualAnalyticsData>
	);
};

export const ShareButtonAnalyticsWrapperWithGating = componentWithCondition(
	() => fg('jsw-smart-access-extended-analytics-enabled'),
	ShareButtonAnalyticsWrapper,
	Fragment,
);

export const ShareButtonSync = (props: Props) =>
	fg('migrate_share_dialog_error_boundary') ? (
		<ShareButtonAnalyticsWrapperWithGating>
			<JSErrorBoundary id="async.share.button" packageName="share-button" fallback="unmount">
				<SyncShareButtonBase {...props} />
			</JSErrorBoundary>
		</ShareButtonAnalyticsWrapperWithGating>
	) : (
		<ErrorBoundary id="async.share.button" packageName="share-button">
			<ShareButtonAnalyticsWrapperWithGating>
				<SyncShareButtonBase {...props} />
			</ShareButtonAnalyticsWrapperWithGating>
		</ErrorBoundary>
	);

export const ShareButton = (props: Props) =>
	fg('migrate_share_dialog_error_boundary') ? (
		<ShareButtonAnalyticsWrapperWithGating>
			<JSErrorBoundary id="async.share.button" packageName="share-button" fallback="unmount">
				<Placeholder
					name="share-button"
					fallback={
						props.renderCustomTriggerButton === undefined ? (
							<ShareLoadingPlaceholder triggerButtonStyle={props.triggerButtonStyle} />
						) : (
							<props.renderCustomTriggerButton isDisabled onClick={noop} />
						)
					}
				>
					<ShareButtonBase {...props} />
				</Placeholder>
			</JSErrorBoundary>
		</ShareButtonAnalyticsWrapperWithGating>
	) : (
		<ErrorBoundary id="async.share.button" packageName="share-button">
			<Placeholder
				name="share-button"
				fallback={
					props.renderCustomTriggerButton === undefined ? (
						<ShareLoadingPlaceholder triggerButtonStyle={props.triggerButtonStyle} />
					) : (
						<props.renderCustomTriggerButton isDisabled onClick={noop} />
					)
				}
			>
				<ShareButtonAnalyticsWrapperWithGating>
					<ShareButtonBase {...props} />
				</ShareButtonAnalyticsWrapperWithGating>
			</Placeholder>
		</ErrorBoundary>
	);

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { Props as ShareButtonProps };

type ShareButtonAsyncProps = Props & {
	errorBoundaryId: string;
	teamName: string;
};

export const ShareButtonAsync = (props: ShareButtonAsyncProps) => {
	const { Panel, trigger } = usePanel();
	const entryPointParams = useMemo(() => ({}), []);
	const entryPointProps = useMemo(() => ({ ...props }), [props]);
	const [isOpen, setIsOpen] = useState(false);
	const { formatMessage } = useIntl();

	return (
		<JSErrorBoundary
			id={props.errorBoundaryId}
			packageName="jiraShareButton"
			teamName={props.teamName}
			fallback="unmount"
		>
			<Trigger
				trigger={trigger}
				entryPoint={shareButtonEntryPoint}
				entryPointParams={entryPointParams}
				entryPointProps={entryPointProps}
			>
				{({ ref }) =>
					!isOpen && (
						<IconButton
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							ref={ref as Ref<HTMLButtonElement>}
							testId="share-button.button"
							label={formatMessage(messages.shareTriggerButtonText)}
							appearance="default"
							icon={() => <ShareIconNew spacing="spacious" label="" color={token('color.icon')} />}
							onClick={() => setIsOpen(true)}
						/>
					)
				}
			</Trigger>
			<Panel />
		</JSErrorBoundary>
	);
};
